<template>
  <v-layout :column="loads.length || loadingBookedLoads">
    <v-flex v-if="!appLoading && !loadingBookedLoads && !loads.length">
      <empty-state :message="`You don't have any ${loadsDescriptor} loads.`" />
    </v-flex>
    <template v-else>
      <div v-if="isMobile">
        <v-card>
          <v-card-title class="py-0">
            <v-layout column>
              <label>
                {{ loadsAccurateSince }}
              </label>
            </v-layout>

            <v-btn fab small flat right
                   color="accent"
                   @click="$emit('refresh')">
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-card-title>
        </v-card>

        <v-card v-for="load in filteredLoads" :key="load.id" class="xs12 my-2">

          <v-card-title>
            <v-layout column>
              <h6 class="title">
                {{load.humanId}}
              </h6>
            </v-layout>

            <v-btn block color="accent" @click="viewLoadDetail(load)">
              <v-icon size="18" color="white">visibility</v-icon>
              <span class="pl-2">View Load</span>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <b>From:</b> {{loadDetails(load) | originName}}<br/>
            <b>To:</b> {{loadDetails(load) | destinationName}}<br/><br/>

            <p>
              <b>Pickup Time:</b>
              {{loadModel.notificationPickupTime(load).value }}
                <span class="font-italic" v-if="load.status === loadStatus.SCHEDULED" >
                  ({{ loadModel.notificationPickupTime(load).providedBy }})
                </span>
                <span v-else>
                  {{ loadModel.notificationDeliveryTime(load).value }}
                </span>
            </p>

            <p>
              <load-distance :load-details="load"></load-distance>
            </p>

            <p>
              <b>Carrier:</b>
              {{bookedLoadHaulingCompany(load).name}}
              <span class="font-italic" v-if="driverId(load)">
            {{ driverName(load) }}
          </span>
              <span class="font-italic" v-else>(no driver assigned)</span>
            </p>
            <v-select-load-driver :load="load"
                                  @refresh="$emit('updateLoad', $event)"
                                  :company="company">
            </v-select-load-driver>

            <span v-if="loadModel.requiresWashout(load)">
            <icon-base width="25" height="25" icon-color="#352208" icon-name="hose">
              <icon-hose />
            </icon-base>
        </span>
            <span v-if="loadModel.requireWoodChips(load)">
          <icon-base width="25" height="25" icon-color="#352208"
                     viewBox="0 0 5 5" icon-name="wood">
            <icon-wood />
          </icon-base>
        </span>
            <span v-if="loadModel.requireWeatherboards(load)">
              <icon-base width="25" height="25" icon-name="cold" icon-color="#352208">
                <icon-cold />
              </icon-base>
            </span>
          </v-card-text>

        </v-card>
      </div>
      <template v-else>

        <v-master-detail-pane :selected="selectedLoad"
                              @reset="selectLoad(null)">
          <template slot="master">
            <v-load-nav-list :search-prompt="searchPrompt"
                             :title="`${loadsDescriptor} (${loads.length})`"
                             :accurate-since="loadsAccurateSince"
                             :loads="filteredLoads"
                             :carrierLoads="groupedByCarrierLoads"
                             :loading="loadingBookedLoads"

                             @update:searchTerm="search = $event"
                             @refresh="refreshBookedLoads">

              <template slot="subheader">
                <v-subheader class="sort-btn" @click="toggleSort()">
                  <v-icon class="pr-2" color="primary">
                    {{ arrivals ? 'flight_land' : 'flight_takeoff' }}
                  </v-icon>
                  <span>{{ arrivals ? 'Arrival' : 'Departure' }} Time</span>

                  <v-spacer/>
                  <v-icon color="accent" class="pl-3">
                    event
                  </v-icon>
                  <v-icon v-if="sortDesc"
                          color="accent">
                    arrow_drop_down
                  </v-icon>
                  <v-icon v-else
                          color="accent">
                    arrow_drop_up
                  </v-icon>
                </v-subheader>
              </template>

              <template slot="loadTile"
                        slot-scope="{ idx, load }">
                <v-list-tile :key="`unassigned_load_${idx}_${load.listingHumanId}`"
                             @click="selectLoad(load)"
                             :class="[{
                              'primary lighten-1': isSelectedLoad(load)}, loadStyleClass(load)
                              ]"
                             :inactive="isSelectedLoad(load)"
                             ripple avatar>

                  <v-list-tile-content>
                    <v-list-tile-title>{{
                      Loads.loadNickname(load) || _.get(load, 'sponsor.name', '...')
                    }}</v-list-tile-title>
                    <v-list-tile-sub-title class="text--primary">
                      {{ load.humanId }}: {{ load.pickupTime | formatDay }}
                    </v-list-tile-sub-title>
                    <v-list-tile-sub-title>
                      {{Loads.haulingCompany(load).name}}<span v-if="load.tacticalInfo.assignedTruckingCompanyDriverName">,
                        {{load.tacticalInfo.assignedTruckingCompanyDriverName}}</span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>

                  <v-list-tile-avatar :size="30">
                    <bqa-certificate-icon responsive :driver="bookedLoadDriverInfo(load)">
                    </bqa-certificate-icon>
                  </v-list-tile-avatar>
                </v-list-tile>
                <v-divider :key="`unassigned_divider_${idx}_${load.listingHumanId}`"/>
              </template>

              <template slot="carrierLoadsTile" slot-scope="{ idx, loads, carrier }">
                <v-list-group :key="`groupedBy_${idx}`">
                  <v-list-tile slot="activator">
                    <v-list-tile-action>
                      <v-chip color="accent"
                              text-color="white" small>
                        {{ loads.length }}
                      </v-chip>
                    </v-list-tile-action>
                    <v-list-tile-title>{{ carrier }}</v-list-tile-title>
                  </v-list-tile>
                  <template v-for="load in loads" >
                    <v-list-tile :key="`grouped_load_${load.humanId}`"
                                 @click="selectLoad(load)"
                                 :class="{'primary lighten-1': isSelectedLoad(load)}"
                                 :inactive="isSelectedLoad(load)"
                                 ripple avatar>

                      <v-list-tile-content>
                        <v-list-tile-title v-if="Loads.loadNickname(load)">
                          {{ Loads.loadNickname(load) }}
                        </v-list-tile-title>
                        <v-list-tile-sub-title class="text--primary">
                          {{ load.humanId }}: {{ load.pickupTime | formatDay }}
                        </v-list-tile-sub-title>
                        <v-list-tile-sub-title>
                          {{Loads.loadNickname(load) || load.sponsor.name}}
                          <span v-if="load.tacticalInfo.assignedTruckingCompanyDriverName">, {{load.tacticalInfo.assignedTruckingCompanyDriverName}}</span>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>

                      <v-list-tile-avatar :size="30">
                        <bqa-certificate-icon responsive :driver="bookedLoadDriverInfo(load)">
                        </bqa-certificate-icon>
                      </v-list-tile-avatar>

                    </v-list-tile>
                    <v-divider :key="`load_divider_${load.humanId}`"/>
                  </template>
                </v-list-group>
              </template>
            </v-load-nav-list>
          </template>

          <template slot="detail">
            <load-detail :currentLoad="selectedLoad" v-if="selectedLoad"
                         class="pl-2"
                         :company="company"
                         :loading="loadingLoad"
                         @refreshLoad="refreshSelectedLoad()">
            </load-detail>

          </template>
        </v-master-detail-pane>

      </template>
    </template>
  </v-layout>
</template>

<script>

import _ from 'lodash';
import API from '@/api';
import moment from 'moment';
import { mapState, mapGetters } from 'vuex';
import Model from '@/types/models/';
import { loadStatus } from '@/types';

import companyNamesMixin from '@/mixins/companyNames';
import refreshPageMixin from '@/mixins/refreshPageMixin';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import facilityFlightBoardMixin from '@/mixins/facilityFlightBoardMixin';
import filterLoads from '@/mixins/filterLoads';

import * as Loads from '@/types/models/load';

import IconBase from '@/components/IconBase';
import IconCold from '@/components/icons/IconCold';
import IconHose from '@/components/icons/IconHose';
import IconWood from '@/components/icons/IconWood';
import IconPdf from '@/components/icons/IconPDF';
import LoadDetail from '@/components/dashboard/simple/LoadDetail';
import VSelectLoadDriver from '@/components/SelectLoadDriver';
import LoadDistance from '@/components/InfoWidgets/LoadDistance';
import EmptyState from '@/components/EmptyState';
import VLoadNavList from '@/components/dashboard/simple/LoadNavList';
import VMasterDetailPane from '@/components/MasterDetailPane';
import BqaCertificateIcon from '@/components/BqaCertificateIcon';

export default {
  name: 'VFacilityFlightBoard',
  components: {
    BqaCertificateIcon,
    IconBase,
    IconCold,
    IconHose,
    IconWood,
    IconPdf,
    LoadDistance,
    EmptyState,
    LoadDetail,
    VLoadNavList,
    VMasterDetailPane,
    VSelectLoadDriver,
  },
  mixins: [
    bookedLoadMixin,
    companyNamesMixin,
    refreshPageMixin,
    facilityFlightBoardMixin,
    filterLoads,
  ],
  props: {
    loadingList: {
      type: Boolean,
      default() {
        return false;
      },
    },
    company: {
      type: Object,
      default() {
        return {};
      },
    },
    arrivals: {
      type: Boolean,
      required: false,
      default: false,
    },
    departures: {
      type: Boolean,
      required: false,
      default: false,
    },
    mobileSearch: {
      type: String,
      default: null,
    },
    mobileSortValue: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    loadsAssignedAt: null,
    sortDesc: true,
    selectedLoad: null,
    checkinBtnLoading: false,
    loadingProfile: false,
    search: '',
    loadingLoad: false,
    pagination: {
      sortBy: null,
      descending: true,
    },

  }),
  watch: {
    mobileSearch(newVal) {
      this.search = newVal;
    },
    mobileSortValue(newVal) {
      this.sortValue = newVal;
    },
    selectedLoad(newVal) {
      if (_.get(newVal, 'humanId', false)) {
        this.$router.push({ params: { loadId: newVal.humanId } });
      }
    },
  },
  computed: {
    ...mapState('applications', {
      appLoading: 'loading',
    }),
    ...mapState('profiles', {
      userProfile: 'userProfile',
    }),
    ...mapGetters('profiles', {
      isLtnAdmin: 'isLtnAdmin',
    }),
    ...mapGetters('bookedLoads', {
      bookedLoadsTime: 'timestamp',
      loadingLoads: 'loadingLoads',
    }),
    loadModel() {
      return Model.load;
    },
    loadStatus() {
      return loadStatus;
    },
    loadsDescriptor() {
      if (this.arrivals) {
        return 'Arriving';
      }
      if (this.departures) {
        return 'Departing';
      }

      throw new TypeError('Facility Flight Board only understands arrivals and departures');
    },
    searchPrompt() {
      if (this.arrivals) {
        return 'Search Arrivals';
      }

      if (this.departures) {
        return 'Search Departures';
      }

      throw new TypeError('Facility Flight Board only understands arrivals and departures');
    },
    loadsAccurateSince() {
      const since = this.bookedLoadsTime;

      if (this.isLoadingBookedLoads || since === null) {
        return 'Loading ...';
      }

      return `Updated ${this.$options.filters.formatTimeStamp(since)}`;
    },

    loads() {
      if (this.arrivals) {
        return this.facilityFlightBoardArrivals(this.company);
      }

      if (this.departures) {
        return this.facilityFlightBoardDepartures(this.company);
      }

      throw new TypeError('Facility Flight Board understands arrivals and departures');
    },
    Loads() {
      return Loads;
    },
    loadingBookedLoads() {
      return this.loadingLoads || this.loadingList;
    },
    groupedByCarrierLoads() {
      const nameSorted = _.sortBy(this.filteredLoads, load => _.get(Loads.haulingCompany(load), 'name', ''));
      return _.groupBy(nameSorted, load => _.get(Loads.haulingCompany(load), 'name', ''));
    },
    _() {
      return _;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    filteredLoads() {
      const filtered = this.sortLoads(
        this.filterLoads(this.loads, this.search, this.filter),
        this.sortValue);
      const sort = this.sortDesc ? 'desc' : 'asc';
      if (this.departures) {
        return _.orderBy(filtered, [load =>
          moment(load.pickupTime, moment.ISO_8601).unix()], [sort]);
      }
      return _.orderBy(filtered, [load =>
        moment(load.deliveryTime, moment.ISO_8601).unix()], [sort]);
    },
  },
  methods: {
    ...mapGetters('profiles', {
      findCompanyAccount: 'findCompanyAccount',
    }),
    updateSelectedLoadById(list, loadId) {
      if (list && list.length && loadId) {
        // Update selection
        const selectedIndex = _.findIndex(list, load => load.humanId === loadId);
        this.selectLoad(selectedIndex > -1 ? list[selectedIndex] : null);
      }
      if (!loadId) {
        this.selectLoad(null);
      }
    },
    isSelectedLoad(load) {
      return load.humanId === _.get(this.selectedLoad, 'humanId', null);
    },
    async selectLoad(load) {
      if (this.isMobile && load) {
        this.$router.push({
          name: 'load.detail',
          params: { loadId: load.humanId },
        });
      } else {
        this.selectedLoad = load;
      }
    },
    async refreshSelectedLoad() {
      this.loadingLoad = true;

      try {
        const apiResponse = await API.load.getLoad({ loadId: this.selectedLoad.humanId });
        this.$store.dispatch('bookedLoads/updateLoad', apiResponse.data);

        const updated = apiResponse.data;
        this.replaceBookedLoad(updated);
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingLoad = false;
      }
    },
    loadDetails(load) {
      return Loads.listingDetails(load);
    },
    driverId(load) {
      return Loads.assignedTruckingCompanyDriverUserId(load);
    },
    driverName(load) {
      return Loads.assignedTruckingCompanyDriverName(load);
    },
    normalizeLoads(loads) {
      _.forEach(loads, (load) => {
        // eslint-disable-next-line no-param-reassign
        load.sponsor = load.tacticalInfo.loadDetails.customerCompany || load.tacticalInfo.loadDetails.listingCompany;
        // eslint-disable-next-line no-param-reassign
        load.pickupTime = load.tacticalInfo.estimatedPickup || load.tacticalInfo.scheduledPickupTime;
        // eslint-disable-next-line no-param-reassign
        load.deliveryTime = _.get(load, 'tacticalInfo.delivery.when', false) || load.tacticalInfo.estimatedDelivery;
      });

      return loads;
    },
    loadStyleClass(load) {
      return this.bookedLoadStyleClass(this.company, load);
    },
    filterLoads(items, searchString, filter) {
      const search = searchString.toString().toLowerCase();
      this.$ga.event({
        eventCategory: 'search',
        eventAction: 'search-load',
        eventLabel: search,
      });

      return items.filter(load =>
        filter(load.humanId, search) ||
        filter(load.sponsor.name, search) ||
        filter(Loads.loadNickname(load), search) ||
        filter(Loads.billOfLadingNumber(load), search) ||
        filter(Loads.listingCompany(load).name, search) ||
        filter(Loads.haulingCompany(load).name, search) ||
        filter(Loads.pickupCompany(load).name, search) ||
        filter(Loads.pickupLocationChute(load).companyName, search) ||
        filter(Loads.pickupLocationChute(load).label, search) ||
        filter(Loads.pickupLocationGooglePlace(load).placeName, search) ||
        filter(Loads.pickupLocationGooglePlace(load).formattedAddress, search) ||
        filter(Loads.dropoffCompany(load).name, search) ||
        filter(Loads.dropoffLocationChute(load).companyName, search) ||
        filter(Loads.dropoffLocationChute(load).label, search) ||
        filter(Loads.dropoffLocationGooglePlace(load).placeName, search) ||
        filter(Loads.dropoffLocationGooglePlace(load).formattedAddress, search) ||
        filter(Loads.assignedTruckingCompanyDriverName(load), search));
    },
    filter(val, search) {
      return val != null && typeof val !== 'boolean' && val.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1;
    },
    onRefresh() {
      if (this.selectedLoad) {
        this.refreshSelectedLoad();
      }
    },
    refreshBookedLoads() {
      this.$store.dispatch('bookedLoads/getBookedLoads', this.company.id);
    },
    toggleSort() {
      this.sortDesc = !this.sortDesc;
    },

  },
  created() {
    if (this.arrivals === this.departures) {
      throw new TypeError("Flight board must specify exactly one of 'departures' or 'arrivals'");
    }

    this.normalizeLoads(this.loads);
  },
};
</script>

<style scoped>
  .scroll-section {
    height: calc(100vh - 225px);
    overflow: auto;
  }

  .pickup-col {
    width: 200px;
  }

  .id-col {
    position: relative;
    padding-left: 35px !important;
  }

  .expand-icon {
    position: absolute;
    left: 5px;
    top: auto;
    bottom: auto;
    margin-top: 10px;
  }

  .miles-col {
    width: 200px;
  }

</style>

<style>
  .load-id-header {
    padding-left: 35px !important;
  }

  .sort-btn {
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/simple/FacilityFlightBoard.vue